import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Box, Typography, IconButton, Grid } from '@mui/material';
import { Visibility } from '@mui/icons-material';

import CustomPhoneInput from '../CustomInputs/CustomPhoneInput';
import { trackPhoneReveal } from '../../services/datadog';
import { maskPhoneNumber, formatPhoneNumber } from '../../utils/phoneUtils';

const MaskedPhone = ({
  number,
  editable,
  entityType,
  context,
  setData,
  path,
  label,
  grid,
  xs,
  sm,
  md,
  lg,
  xl,
  classGrid,
  ...props
}) => {
  const user = useSelector(state => state.connectedUser);
  const [isRevealed, setIsRevealed] = useState(false);

  // Wrapper pour setData qui révèle automatiquement quand on modifie
  const handleSetData = (path, value, ...args) => {
    setIsRevealed(true);
    if (setData) {
      setData(path, value, ...args);
    }
  };

  if (!number || number.trim() === '') {
    if (!editable) {
      return <Typography>—</Typography>;
    }

    return (
      <CustomPhoneInput
        value={number}
        setData={handleSetData}
        path={path}
        label={label}
        grid={grid}
        xs={xs}
        sm={sm}
        md={md}
        lg={lg}
        xl={xl}
        classGrid={classGrid}
        {...props}
      />
    );
  }

  const handleReveal = () => {
    trackPhoneReveal({
      userId: user._id,
      userName: user.login,
      phoneNumber: number,
      entityType,
      context
    });

    setIsRevealed(true);
  };

  if (editable && !isRevealed) {
    const noOp = () => {
      // No-op function for readonly mode
    };
    const content = (
      <Box display="flex" alignItems="flex-end" gap={1}>
        <Box flex={1}>
          <CustomPhoneInput
            value={maskPhoneNumber(number)}
            setData={noOp}
            path={path}
            label={label}
            disabled
            {...props}
          />
        </Box>
        <IconButton
          onClick={handleReveal}
          size="small"
          color="primary"
          sx={{ mb: 0.5 }}
        >
          <Visibility fontSize="small" />
        </IconButton>
      </Box>
    );

    return grid ? (
      <Grid
        item
        xs={xs}
        sm={sm}
        md={md}
        lg={lg}
        xl={xl}
        className={classGrid}
      >
        {content}
      </Grid>
    ) : content;
  }

  if (editable && isRevealed) {
    return (
      <CustomPhoneInput
        value={number}
        setData={handleSetData}
        path={path}
        label={label}
        grid={grid}
        xs={xs}
        sm={sm}
        md={md}
        lg={lg}
        xl={xl}
        classGrid={classGrid}
        {...props}
      />
    );
  }

  if (!editable && !isRevealed) {
    const content = (
      <Box display="flex" alignItems="center" gap={1}>
        <Typography>{maskPhoneNumber(number)}</Typography>
        <IconButton onClick={handleReveal} size="small" color="primary">
          <Visibility fontSize="small" />
        </IconButton>
      </Box>
    );

    return grid ? (
      <Grid
        item
        xs={xs}
        sm={sm}
        md={md}
        lg={lg}
        xl={xl}
        className={classGrid}
      >
        {content}
      </Grid>
    ) : content;
  }

  const content = <Typography>{formatPhoneNumber(number)}</Typography>;

  return grid ? (
    <Grid
      item
      xs={xs}
      sm={sm}
      md={md}
      lg={lg}
      xl={xl}
      className={classGrid}
    >
      {content}
    </Grid>
  ) : content;
};

MaskedPhone.propTypes = {
  number: PropTypes.string.isRequired,
  editable: PropTypes.bool.isRequired,
  entityType: PropTypes.oneOf([
    'client', 'artisan', 'billing', 'btob'
  ]).isRequired,
  context: PropTypes.string.isRequired,
  setData: PropTypes.func,
  path: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  label: PropTypes.string,
  grid: PropTypes.bool,
  xs: PropTypes.number,
  sm: PropTypes.number,
  md: PropTypes.number,
  lg: PropTypes.number,
  xl: PropTypes.number,
  classGrid: PropTypes.string
};

export default MaskedPhone;
